import React, { useEffect, useMemo, useRef, useState } from "react";
import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { noteNeedsPurchase, promptNoteSubscription, noteDetailFlow } from './notesAccess';
import { useNotesList } from '../../hooks/useNotesList';
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QbankDetail.css";
import HeaderProfileSection from "../HeaderProfileSection";

import NotesReportModal from "./NotesReportModal";
import NotesStickyNotesPopup from "./NotesStickyNotesPopup";
import NotesViewModal from "./NotesViewModal";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useNoteDetail, loadNoteDetail } from "../../hooks/useNoteDetail";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import { buildQuestionBankFlowState, getStoredQuestionBankFlowState, persistQuestionBankFlowState } from "../../questionBank/qbankFlowState";
import { storeNoteTime, toggleNoteBookmark, toggleNoteLike, toggleNoteReadStatus } from "../../services/notesFeedbackService";

import "./Notesdetails.css";
import { CONTENT_ROUTES } from "../contentRoutes";

const buildFooterStats = (note) => [
  {
    key: "bookmark",
    icon: note?.isBookmarked ? "bi-bookmark-fill" : "bi-bookmark",
    label: "Bookmark",
  },
  {
    key: "like",
    icon: "bi-heart",
    label: "Like",
    value: note?.likeCount || 0,
  },
  {
    key: "view",
    icon: "bi-eye",
    label: "View",
    value: note?.viewCount || 0,
    modal: "view",
  },
  {
    key: "report",
    icon: "bi-exclamation-circle",
    label: "Report",
    value: note?.reportCount || "0",
    modal: "report",
  },
  {
    key: "notes",
    icon: "bi-journal-text",
    label: "Notes",
    value: note?.personalNotesCount || 0,
    modal: "notes",
  },
];

function formatDuration(totalSeconds = 0) {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return [hours, minutes, seconds].map((part) => String(part).padStart(2, "0")).join(":");
}

const NotesDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const navigationBusyRef = useRef(false);
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(
    () => buildQuestionBankFlowState(location.state, getStoredQuestionBankFlowState()),
    [location.state]
  );
  const stateId = resolveQuestionBankStateId({ locationState: flowState, sidebarData }) || 13;
  const subjectId = Number(flowState?.selectedSubject?.id || flowState?.subject_id || 1);
  const noteId = Number(flowState?.selectedNote?.id || flowState?.note_id || 1);
  const adRewardId = flowState.ad_reward_id || flowState.selectedNote?.raw?.ad_reward_id;
  const noteParams = useMemo(
    () => ({ states_id: stateId, subject_id: subjectId, category_id: 4, note_id: noteId,
      ...contentTopicRequest(flowState), ...(adRewardId ? { ad_reward_id: adRewardId } : {}) }),
    [noteId, stateId, subjectId, flowState, adRewardId]
  );

  useEffect(() => {
    persistQuestionBankFlowState(flowState);
  }, [flowState]);
  const { data: noteDetail, isLoading, isError, error } = useNoteDetail(noteParams, { enabled: Boolean(noteId) });
  const noteData = noteDetail?.note || {};
  const [interactionState, setInteractionState] = useState({});
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [fontScale, setFontScale] = useState(1);
  const elapsedSecondsRef = useRef(0);
  const lastStoredSecondsRef = useRef(-1);
  const stopTimerRef = useRef(() => {});
  const detailNote = { ...noteData, ...interactionState };
  const footerStats = buildFooterStats(detailNote);
  const fileUrl = detailNote?.fileUrl || "";
  const isLockedNote = noteNeedsPurchase(detailNote, true);
  const promptPurchase = (note = detailNote, metadata = noteDetail?.raw) =>
    promptNoteSubscription(note, { ...flowState, ...noteParams, stateId, subjectId, categoryId: 4 }, navigate, metadata);
  const notesFeedbackItem = useMemo(
    () => ({
      id: detailNote?.id,
      title: detailNote?.title,
      description: detailNote?.description,
      viewCount: detailNote?.viewCount,
      reportCount: detailNote?.reportCount,
      notesCount: detailNote?.personalNotesCount,
    }),
    [detailNote?.description, detailNote?.id, detailNote?.personalNotesCount, detailNote?.reportCount, detailNote?.title, detailNote?.viewCount]
  );
  /* ==============================
     Dropdown
  ============================== */

  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const profileMenuRef = useRef(null);

  // Retained for the existing text-note fallback renderer.
  const pdfContainerRef = useRef(null);

  /* ==============================
     Modals
  ============================== */

  const [activeModal, setActiveModal] = useState(null);

  /* ==============================
     Question Palette
  ============================== */

  const [isPaletteDrawerOpen, setIsPaletteDrawerOpen] =
    useState(false);
  const [drawerPage, setDrawerPage] = useState(Number(flowState.listQueryState?.currentPage || 1));
  const drawerRef = useRef(null);
  useEffect(() => {
    if (!isPaletteDrawerOpen) return;
    const previousFocus = document.activeElement;
    drawerRef.current?.querySelector('button')?.focus();
    return () => previousFocus?.focus?.();
  }, [isPaletteDrawerOpen]);
  const drawerQuery = useNotesList({ states_id: stateId, subject_id: subjectId, category_id: 4,
    ...contentTopicRequest(flowState), page: drawerPage }, { enabled: isPaletteDrawerOpen });



  /* ==============================
     Sticky Notes
  ============================== */

  const [notes, setNotes] = useState([]);

  const [isNotesOpen, setIsNotesOpen] =
    useState(true);

  useEffect(() => {
    setInteractionState({});
    setElapsedSeconds(Number(noteData?.timerSeconds || 0));
    lastStoredSecondsRef.current = -1;
  }, [noteData?.id, noteData?.timerSeconds]);

  useEffect(() => {
    elapsedSecondsRef.current = elapsedSeconds;
  }, [elapsedSeconds]);

  useEffect(() => {
    if (!detailNote?.id || !subjectId) {
      return undefined;
    }

    let timer = null;

    const startTimer = () => {
      if (timer !== null) {
        return;
      }

      timer = window.setInterval(() => {
        setElapsedSeconds((current) => current + 1);
      }, 1000);
    };

    const stopTimer = () => {
      if (timer === null) {
        return;
      }

      window.clearInterval(timer);
      timer = null;
    };

    stopTimerRef.current = stopTimer;

    const persistTime = () => {
      const timerSeconds = Math.max(1, Math.floor(elapsedSecondsRef.current));
      if (lastStoredSecondsRef.current === timerSeconds) {
        return;
      }

      lastStoredSecondsRef.current = timerSeconds;
      storeNoteTime({
        note_id: Number(detailNote.id),
        subject_id: Number(subjectId),
        timer_seconds: timerSeconds,
        is_view: 1,
        view_status: 1,
      }).catch(() => {
        lastStoredSecondsRef.current = -1;
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopTimer();
        persistTime();
        return;
      }

      startTimer();
    };

    if (document.visibilityState === "visible") {
      startTimer();
    }

    window.addEventListener("pagehide", persistTime);
    window.addEventListener("popstate", persistTime);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopTimer();
      stopTimerRef.current = () => {};
      window.removeEventListener("pagehide", persistTime);
      window.removeEventListener("popstate", persistTime);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      persistTime();
    };
  }, [detailNote?.id, subjectId]);

  /* ==============================
     Close Dropdown Outside Click
  ============================== */

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(
          event.target
        )
      ) {
        setIsProfileOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, []);

  /* ==============================
     Lock Body Scroll
  ============================== */

  useEffect(() => {
    if (!isPaletteDrawerOpen) {
      return undefined;
    }

    const originalOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        originalOverflow;
    };
  }, [isPaletteDrawerOpen]);

  /* ==============================
     Escape Palette
  ============================== */

  useEffect(() => {
    if (!isPaletteDrawerOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsPaletteDrawerOpen(false);
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [isPaletteDrawerOpen]);

  /* ==============================
     Load PDF.js + Render PDF
  ============================== */

  useEffect(() => {
    // The signed GCS URL can be embedded by the browser, but PDF.js cannot fetch it
    // because the storage response does not include a CORS header.
    if (["F", "BOTH"].includes(detailNote?.type) && fileUrl) {
      return undefined;
    }

    let isMounted = true;

    const PDF_URL = fileUrl;

    const renderPdf = async () => {
      try {
        if (detailNote?.type === "T" || !PDF_URL ||
          !window.pdfjsLib ||
          !pdfContainerRef.current
        ) {
          return;
        }

        const container =
          pdfContainerRef.current;

        container.innerHTML = "";

        const loadingElement =
          document.createElement("div");

        loadingElement.className =
          "pdf-loading";

        loadingElement.innerText =
          "Loading PDF...";

        container.appendChild(
          loadingElement
        );

        const loadingTask =
          window.pdfjsLib.getDocument(
            PDF_URL
          );

        const pdf =
          await loadingTask.promise;

        if (!isMounted) {
          return;
        }

        container.innerHTML = "";

        /*
          Render every page
        */

        for (
          let pageNumber = 1;
          pageNumber <= pdf.numPages;
          pageNumber++
        ) {
          const page =
            await pdf.getPage(
              pageNumber
            );

          /*
            PDF page scale

            Change 1.5 to:
            1.2 = smaller
            1.5 = normal
            2   = larger
          */

          const viewport =
            page.getViewport({
              scale: 1.5,
            });

          const canvas =
            document.createElement(
              "canvas"
            );

          const context =
            canvas.getContext("2d");

          canvas.width =
            viewport.width;

          canvas.height =
            viewport.height;

          canvas.className =
            "pdf-canvas";

          /*
            Wrapper for center alignment
          */

          const pageWrapper =
            document.createElement(
              "div"
            );

          pageWrapper.className =
            "pdf-page-wrapper";

          pageWrapper.appendChild(
            canvas
          );

          container.appendChild(
            pageWrapper
          );

          await page.render({
            canvasContext: context,
            viewport,
          }).promise;
        }
      } catch (error) {
        if (
          pdfContainerRef.current
        ) {
          pdfContainerRef.current.innerHTML = `
            <div class="pdf-error">
              Unable to load PDF
            </div>
          `;
        }
      }
    };

    /*
      Load PDF.js CDN
    */

    const loadPdfJs = () => {
      if (window.pdfjsLib) {
        renderPdf();
        return;
      }

      const existingScript =
        document.getElementById(
          "pdfjs-script"
        );

      if (existingScript) {
        existingScript.addEventListener(
          "load",
          renderPdf
        );

        return;
      }

      const script =
        document.createElement(
          "script"
        );

      script.id = "pdfjs-script";

      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";

      script.async = true;

      script.onload = () => {
        if (
          window.pdfjsLib
        ) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

          renderPdf();
        }
      };

      script.onerror = () => {
        if (
          pdfContainerRef.current
        ) {
          pdfContainerRef.current.innerHTML = `
            <div class="pdf-error">
              Unable to load PDF
            </div>
          `;
        }
      };

      document.body.appendChild(
        script
      );
    };

    loadPdfJs();

    return () => {
      isMounted = false;
    };
  }, [detailNote?.type, fileUrl]);

  /* ==============================
     Footer Actions
  ============================== */

  const handleBookmark = async () => {
    if (!detailNote?.id) {
      return;
    }

    const { payload } = await toggleNoteBookmark({ notes_id: Number(detailNote.id) });
    setInteractionState((current) => ({
      ...current,
      isBookmarked: Number(payload?.bookmark_status) === 1,
    }));
  };

  const handleLike = async () => {
    if (!detailNote?.id) {
      return;
    }

    const wasLiked = Boolean(detailNote.isLiked);
    setInteractionState((current) => ({
      ...current,
      isLiked: !wasLiked,
      likeCount: Math.max(0, Number(detailNote.likeCount || 0) + (wasLiked ? -1 : 1)),
    }));

    try {
      const { payload } = await toggleNoteLike({ notes_id: Number(detailNote.id) });
      setInteractionState((current) => ({
        ...current,
        isLiked: Number(payload?.like_status) === 1,
        likeCount: Number(payload?.total_likes ?? current.likeCount ?? 0),
      }));
    } catch (_error) {
      setInteractionState((current) => ({
        ...current,
        isLiked: wasLiked,
        likeCount: Number(detailNote.likeCount || 0),
      }));
    }
  };

  const handleMarkRead = async () => {
    if (!detailNote?.id || !subjectId) {
      return;
    }

    const { payload } = await toggleNoteReadStatus({
      notes_id: Number(detailNote.id),
      subject_id: Number(subjectId),
    });
    setInteractionState((current) => ({
      ...current,
      isRead: Number(payload?.view_status) === 1,
    }));
  };

  const openFullView = () => {
    if (isLockedNote) {
      void promptPurchase();
      return;
    }

    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleFontSizeChange = () => {
    setFontScale((current) => (current >= 1.2 ? 0.9 : Number((current + 0.1).toFixed(1))));
  };

  const handleDownloadPdf = () => {
    if (!fileUrl) {
      return;
    }

    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  const handleBackNavigation = () => {
    if (detailNote?.id && subjectId) {
      stopTimerRef.current();
      const timerSeconds = Math.max(1, Math.floor(elapsedSecondsRef.current));
      if (lastStoredSecondsRef.current !== timerSeconds) {
        lastStoredSecondsRef.current = timerSeconds;
        storeNoteTime({
          note_id: Number(detailNote.id),
          subject_id: Number(subjectId),
          timer_seconds: timerSeconds,
          is_view: 1,
          view_status: 1,
        }).catch(() => {
          lastStoredSecondsRef.current = -1;
        });
      }
    }

    navigate(flowState?.qbankBackTarget?.pathname || CONTENT_ROUTES.notes, {
      state: flowState?.qbankBackTarget?.state,
    });
  };

  const handleFooterClick = async (item) => {
    if (item.key === "bookmark") {
      await handleBookmark();
      return;
    }

    if (item.key === "like") {
      await handleLike();
      return;
    }

    if (item.modal) {
      setActiveModal(
        item.modal
      );
    }
  };

  /* ==============================
     Palette Actions
  ============================== */

  const handleQuestionPaletteOpen =
    () => {
        setIsPaletteDrawerOpen(
          true
        );
    };

  /* ==============================
     Delete Note
  ============================== */

  const handleDeleteNote = (
    id
  ) => {
    setNotes(
      (prevNotes) =>
        prevNotes.filter(
          (note) =>
            note.id !== id
        )
    );
  };

  const goToNote = async (targetNoteId) => {
    if (!targetNoteId || navigationBusyRef.current || Number(targetNoteId) === noteId) {
      return;
    }
    navigationBusyRef.current = true;
    let target;
    try {
      target = await queryClient.fetchQuery({ queryKey: ['noteDetail', { ...noteParams, note_id: Number(targetNoteId) }],
        queryFn: () => loadNoteDetail({ ...noteParams, note_id: Number(targetNoteId) }), staleTime: 0 });
      if (!target?.note?.id) throw new Error('This note is unavailable.');
      if (noteNeedsPurchase(target.note, true)) {
        await promptPurchase(target.note, target.raw);
        navigationBusyRef.current = false;
        return;
      }
    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Unable to open note', text: error.message });
      navigationBusyRef.current = false;
      return;
    }

    if (detailNote?.id && subjectId) {
      try {
        stopTimerRef.current();
        const timerSeconds = Math.max(1, Math.floor(elapsedSecondsRef.current));
        if (lastStoredSecondsRef.current !== timerSeconds) {
          lastStoredSecondsRef.current = timerSeconds;
        await storeNoteTime({
          note_id: Number(detailNote.id),
          subject_id: Number(subjectId),
          timer_seconds: timerSeconds,
          is_view: 1,
          view_status: 1,
        });
        }
      } catch (_error) {
        // Continue navigation; the timer cleanup retries the same save.
        lastStoredSecondsRef.current = -1;
      }
    }

    const nextFlowState = buildQuestionBankFlowState(noteDetailFlow(target.note, { ...flowState, ...noteParams }, target.raw));
    setIsPaletteDrawerOpen(false);
    persistQuestionBankFlowState(nextFlowState);
    navigate(CONTENT_ROUTES.noteDetail, {
      replace: true,
      state: nextFlowState,
    });
    navigationBusyRef.current = false;
  };

  /* ==============================
     Footer Navigation
  ============================== */

  const renderFooterNav = (
    variant
  ) => (
    <div
      className={`footer-nav-fixed ${variant}`}
    >
      <div
        className="qbank-navRow footerIcon-show"
        aria-label="Question navigation"
      >
        <button
          type="button"
          className="qbank-navBtn qbank-navBtn--prev"
          disabled={!detailNote?.previousId}
          onClick={() => goToNote(detailNote?.previousId)}
        >
          <i
            className="bi bi-arrow-left"
            aria-hidden="true"
          />

          <span>
            Previous
          </span>
        </button>

        <button
          type="button"
          className="qbank-navMenu footer-menu"
          aria-label="Open notes list"
          aria-expanded={
            isPaletteDrawerOpen
          }
          onClick={
            handleQuestionPaletteOpen
          }
        >
          <i className="bi bi-book-half" />
        </button>

        <button
          type="button"
          className="qbank-navBtn qbank-navBtn--next"
          disabled={!detailNote?.nextId}
          onClick={() => goToNote(detailNote?.nextId)}
        >
          <span>
            Next
          </span>

          <i
            className="bi bi-arrow-right"
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="qbank-stats">
        {footerStats.map(
          (stat) => (
            <button
              key={stat.key}
              type="button"
              className="qbank-stat"
              onClick={() =>
                handleFooterClick(
                  stat
                )
              }
            >
              <i
                className={`bi ${stat.icon}`}
              />

              <span>
                {stat.label}
              </span>

              {stat.value && (
                <strong>
                  {stat.value}
                </strong>
              )}
            </button>
          )
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Page Height Setup */}

      <style>
        {`
          .homeDashboard-wrapper .main-content {
            height: 100vh;
            overflow: hidden;
            box-sizing: border-box;
            padding-bottom: 35px;
          }

          .homeDashboard-wrapper .page-content {
            height: 100%;
            overflow: hidden;
            padding-right: 0;
          }
         .data-question-scroll.mt-20 {
           margin-top: 10px;
          }
          .qbank-LeftContent .footer-nav-fixed--desktop {
             padding-top: 15px;
          }
        .qbank-stats {
            margin-top: auto;
        }
       .qbank-navBtn {
          min-height: 42px;
        }
      @media (max-width: 1199px) {
      .qbank-rightContent {
        padding: 0 0px 45px 0px !important;
       }
     }
        `}
      </style>

      <HomeDashboardLayout
        showHeader={false}
      >
        <div
          className="categories-page qbank-detail-page"
          style={{
            background: "#fff",
          }}
        >
          {/* =====================
              PAGE HEADER
          ===================== */}

          <div className="categories-page-header">
            <div>
              <button
                type="button"
                className="categories-back-button"
                onClick={handleBackNavigation}
              >
                <i className="bi bi-chevron-left" />
              </button>
            </div>

            <div>
              <h1 className="categories-page-title">
              <div className="qbank-timer" aria-label="Reading timer">
                <i className="bi bi-clock" aria-hidden="true"></i>
                <span>{formatDuration(elapsedSeconds)}</span></div>
              </h1>
            </div>

            <div>
              <HeaderProfileSection />
            </div>
          </div>

          {/* =====================
              MAIN CONTENT
          ===================== */}

          <div className="qb-Detail-content">
            <div className="d-flex qbank-detail-main">

              {/* =====================
                  LEFT PDF SECTION
              ===================== */}

              <section
                className="qbank-LeftContent"
                aria-label="Notes PDF"
              >
                <div className="qbank-card qbank-card--question">

                  {/* Breadcrumb */}

                  <p className="descriptionMain">
                    {noteDetail?.stateName || "State"}

                    <span>
                      {noteDetail?.categoryName || "Notes"}
                    </span>

                    <span>
                      {noteDetail?.subjectName || "Subject"}
                    </span>
                  </p>

                  {/* Title + Dropdown */}

                  <div className="rapid-fire-header d-flex align-items-center justify-space-between">

                    <h1 className="qb-title">
                      {detailNote?.title || "Loading note..."}
                    </h1>

                    {/* =====================
                        THREE DOT DROPDOWN
                    ===================== */}

                    <div
                      className={`qb-profileWrap ${
                        isProfileOpen
                          ? "is-open"
                          : ""
                      }`}
                      ref={
                        profileMenuRef
                      }
                    >
                      <button
                        className="dot-dropdownn"
                        type="button"
                        aria-expanded={
                          isProfileOpen
                        }
                        aria-haspopup="menu"
                        onClick={() =>
                          setIsProfileOpen(
                            (prev) =>
                              !prev
                          )
                        }
                      >
                        ⋮
                      </button>

                      {isProfileOpen && (
                        <div
                          className="qb-profileMenu notes-drpdwn"
                          role="menu"
                        >
                          <button
                            className="qb-profileMenu__item"
                            role="menuitem"
                            type="button"
                            onClick={handleFontSizeChange}
                          >
                            <div>
                              <i className="bi bi-fonts" />
                            </div>

                            <div>
                              Font Size
                            </div>
                          </button>

                          <button
                            className="qb-profileMenu__item"
                            role="menuitem"
                            type="button"
                            onClick={openFullView}
                          >
                            <div>
                              <i className="fa-solid fa-expand" />
                            </div>

                            <div>
                              Full View
                            </div>
                          </button>

                          {detailNote?.isDownloadable && fileUrl ? (
                            <button
                              className="qb-profileMenu__item"
                              role="menuitem"
                              type="button"
                              onClick={handleDownloadPdf}
                            >
                              <div>
                                <i className="bi bi-download" />
                              </div>
                              <div>Download PDF</div>
                            </button>
                          ) : null}

                          <button
                            className="qb-profileMenu__item"
                            role="menuitem"
                            type="button"
                            onClick={handleMarkRead}
                          >
                             <label className="qbank-guess__check">
                                <input
                                  id="guess-answer"
                                  type="checkbox"
                                  checked={Boolean(detailNote?.isRead)}
                                  readOnly
                                />

                                <span
                                  className="qbank-guess__box"
                                  aria-hidden="true"
                                />
                              </label>

                            <div>
                              Mark Read
                            </div>
                          </button>

                          <button
                            className="qb-profileMenu__item"
                            role="menuitem"
                            type="button"
                            onClick={handleBookmark}
                          >
                            <div>
                              <i className={`bi ${detailNote?.isBookmarked ? "bi-bookmark-fill" : "bi-bookmark"}`} />
                            </div>

                            <div>
                              Bookmark
                            </div>
                          </button>

                          <button
                            className="qb-profileMenu__item"
                            role="menuitem"
                            type="button"
                            onClick={() => setActiveModal("report")}
                          >
                            <div>
                              <i className="fa-regular fa-flag" />
                            </div>

                            <div>
                              Report
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* =====================
                      PDF SECTION
                      ONLY PDF PAGES
                  ===================== */}

                  <div className="data-question-scroll mt-20">
                    {isLoading ? <div className="pdf-loading">Loading note...</div> : null}
                    {isError ? <div className="pdf-error">{error?.response?.data?.message || error?.message || "Unable to load note."}</div> : null}
                    {!isLoading && !isError && isLockedNote ? (
                      <div className="pdf-error">
                        <button type="button" className="notes-external-pdf" onClick={() => promptPurchase()}>
                          Unlock this note
                        </button>
                      </div>
                    ) : null}
                    {!isLoading && !isError && !isLockedNote && ["F", "BOTH"].includes(detailNote?.type) && String(detailNote?.fileDisplay || "I").toUpperCase() !== "E" ? (
                      <iframe
                        className="notes-pdf-frame"
                        src={fileUrl}
                        title={detailNote?.title || "Note PDF"}
                      />
                    ) : null}
                    {!isLoading && !isError && !isLockedNote && ["F", "BOTH"].includes(detailNote?.type) && String(detailNote?.fileDisplay || "I").toUpperCase() === "E" ? (
                      <button type="button" className="notes-external-pdf" onClick={openFullView}>
                        <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                        View PDF
                      </button>
                    ) : null}
                    {!isLoading && !isError && !isLockedNote && ["T", "BOTH"].includes(detailNote?.type) ? <div className="notes-detail-content" style={{ fontSize: `${fontScale}em` }} dangerouslySetInnerHTML={{ __html: detailNote?.description || "<p>No note content available.</p>" }} /> : null}
                  </div>

                  {/* Desktop Footer */}

                  {renderFooterNav(
                    "footer-nav-fixed--desktop"
                  )}
                </div>
              </section>

              {/* =====================
                  RIGHT SIDEBAR
              ===================== */}

              <aside
                className="qbank-rightContent"
                aria-label="Summary and notes"
              >

                {/* Summary */}

                <div className="sumary-notes-container">

                  <div className="sumary-header">
                    <h2>
                      Summary & Notes
                    </h2>

                    <p>
                      Quick recap and sticky notes
                    </p>
                  </div>

                  <div className="sumary-card">

                    <p className="sumary-title">
                      Summary:
                    </p>

                    <p className="sumary-text">
                      {detailNote?.summary || "No summary available."}
                    </p>
                  </div>
                </div>

                {/* Sticky Notes */}

                <section className="qbank-sideCard qbank-sideCard--notes">

                  <div className="qbank-sideCard__head">

                    <h3>
                      Sticky Notes
                    </h3>

                    <button
                      type="button"
                      className="qbank-sideCard__toggle"
                      onClick={() =>
                        setIsNotesOpen(
                          !isNotesOpen
                        )
                      }
                    >
                      <i
                        className={`bi ${
                          isNotesOpen
                            ? "bi-chevron-up"
                            : "bi-chevron-down"
                        }`}
                      />
                    </button>
                  </div>

                  {isNotesOpen && (
                    <div className="qbank-notesList">

                      {notes.map(
                        (
                          note,
                          index
                        ) => (
                          <div
                            className="qbank-note"
                            key={
                              note.id
                            }
                          >

                            <span className="qbank-note__index">
                              {index + 1}.
                            </span>

                            <p className="qbank-note__text">
                              {
                                note.text
                              }
                            </p>

                            <button
                              type="button"
                              className="qbank-note__close"
                              aria-label="Remove note"
                              onClick={() =>
                                handleDeleteNote(
                                  note.id
                                )
                              }
                            >
                              <i className="bi bi-x" />
                            </button>

                          </div>
                        )
                      )}

                    </div>
                  )}

                </section>

              </aside>

            </div>

            {/* Mobile Footer */}

            {renderFooterNav(
              "footer-nav-fixed--mobile"
            )}

          </div>

          {/* Modals */}
          {isPaletteDrawerOpen && <div className="notes-navigation-overlay" onClick={() => setIsPaletteDrawerOpen(false)}>
            <section ref={drawerRef} className="notes-navigation-drawer" role="dialog" aria-modal="true" aria-label="Notes drawer" onClick={event => event.stopPropagation()} onKeyDown={event => {
              if (event.key !== 'Tab') return;
              const buttons = [...event.currentTarget.querySelectorAll('button:not(:disabled)')];
              const first = buttons[0]; const last = buttons[buttons.length - 1];
              if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
              else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
            }}>
              <div className="notes-navigation-drawer__header"><h2>Notes</h2><button type="button" aria-label="Close notes drawer" onClick={() => setIsPaletteDrawerOpen(false)}>×</button></div>
              {drawerQuery.isLoading ? <p role="status">Loading notes...</p> : drawerQuery.error ? <p role="alert">{drawerQuery.error.message}</p> : null}
              {(drawerQuery.data?.notes || []).map(note => <button type="button" key={note.id} className="notes-navigation-drawer__note" aria-current={Number(note.id) === noteId ? 'true' : undefined} onClick={() => goToNote(note.id)}>
                <span>{note.serialNo}. {note.title}</span>{noteNeedsPurchase(note, true) ? <i className="bi bi-lock-fill" aria-label="Paid note" /> : null}
              </button>)}
              <div className="notes-navigation-drawer__pages"><button type="button" disabled={drawerPage <= 1} onClick={() => setDrawerPage(page => page - 1)}>Previous page</button><span>Page {drawerPage}</span><button type="button" disabled={drawerPage >= Number(drawerQuery.data?.pagination?.totalPages || 1)} onClick={() => setDrawerPage(page => page + 1)}>Next page</button></div>
            </section>
          </div>}

          <NotesViewModal
            open={
              activeModal ===
              "view"
            }
            onClose={() =>
              setActiveModal(
                null
              )
            }
            note={notesFeedbackItem}
          />

          <NotesReportModal
            open={
              activeModal ===
              "report"
            }
            onClose={() =>
              setActiveModal(
                null
              )
            }
            note={notesFeedbackItem}
            notesQueryParams={noteParams}
          />

          <NotesStickyNotesPopup
            open={
              activeModal ===
              "notes"
            }
            onClose={() =>
              setActiveModal(
                null
              )
            }
            note={notesFeedbackItem}
            notesQueryParams={noteParams}
          />

        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default NotesDetail;
